import { Link, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { MenuItem } from "@mui/material";
import { Badge } from "react-bootstrap";
import Sidebar from "../../components/sidebar/Sidebar";
import LoadingSpinner from "../../components/LoadingSpinner";
import "../accounts/view/ViewAccountPage.css";
import { MUI_TABLE } from '../../utils/muiTableConfig';
import { Username } from '../../utils/privacyMask';


export default function AccountsPage() {
  const navigate = useNavigate();

  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/account`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        setAccounts(res.data);
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.message || "Failed to fetch accounts");
      }
    };
    fetchData();
  }, []);

  const columns = useMemo(() => [
    { header: 'Username', accessorKey: 'username', Cell: ({ cell }) => <span style={{ fontWeight: 600 }}><Username value={cell.getValue()} /></span> },
    {
      header: 'Type', accessorFn: r => r.accountType === 'reseller' ? 'reseller' : 'customer', size: 110,
      Cell: ({ row }) => {
        const isReseller = row.original.accountType === 'reseller';
        return (
          <Badge bg="" style={{
            background: isReseller ? 'rgba(167,139,250,0.14)' : 'rgba(148,163,184,0.12)',
            color: isReseller ? '#c4b5fd' : '#94a3b8',
            border: `1px solid ${isReseller ? 'rgba(167,139,250,0.35)' : 'rgba(148,163,184,0.25)'}`,
            fontSize: '0.72rem', fontWeight: 600,
          }}>
            {isReseller ? 'Reseller' : 'Customer'}
          </Badge>
        );
      },
    },
    { header: 'Email',    accessorKey: 'email',    Cell: ({ cell }) => <span style={{ color: '#94a3b8' }}>{cell.getValue() || '—'}</span> },
    { header: 'Telegram', accessorKey: 'telegram', Cell: ({ cell }) => cell.getValue() ? <span style={{ color: '#94a3b8' }}>@{cell.getValue()}</span> : <span style={{ color: '#334155' }}>—</span> },
    { header: 'Discord',  accessorKey: 'discord',  Cell: ({ cell }) => <span style={{ color: '#94a3b8' }}>{cell.getValue() || '—'}</span> },
  ], []);

  const table = useMaterialReactTable({
    columns,
    data: accounts ?? [],
    enableColumnFilterModes: true,
    enableColumnOrdering: true,
    enableGrouping: true,
    enableColumnPinning: true,
    enableFacetedValues: true,
    enableRowActions: true,
    enableRowSelection: false,
    enableGlobalFilter: true,
    initialState: {
      showColumnFilters: false,
      showGlobalFilter: false,
      columnPinning: { left: ['mrt-row-expand', 'mrt-row-select'], right: ['mrt-row-actions'] },
    },
    renderRowActionMenuItems: ({ row }) => [
      <MenuItem key="edit"   onClick={() => navigate(`/account/edit/${row.original._id}`)}  sx={{ m: 0, gap: 1 }}>
        <i className="bi bi-pencil-square" style={{ fontSize: '0.9rem', color: '#64748b' }} />Edit
      </MenuItem>,
      <MenuItem key="view"   onClick={() => navigate(`/account/view/${row.original._id}`)}  sx={{ m: 0, gap: 1 }}>
        <i className="bi bi-eye" style={{ fontSize: '0.9rem', color: '#64748b' }} />View
      </MenuItem>,
      <MenuItem key="delete" onClick={() => {}} sx={{ m: 0, gap: 1, color: '#f87171' }}>
        <i className="bi bi-trash" style={{ fontSize: '0.9rem', color: '#f87171' }} />Delete
      </MenuItem>,
    ],
    ...MUI_TABLE,
  });

  if (loading) return <LoadingSpinner />;

  return (
    <>
      <Sidebar title="Accounts" />

      <div className="view-page">
        {error && (
          <div style={{ padding: '12px 16px', marginBottom: 20, background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)', borderRadius: 10, color: '#f87171', fontSize: '0.875rem' }}>
            <i className="bi bi-exclamation-triangle-fill me-2" />{error}
          </div>
        )}

        <div className="view-panel">
          <div className="view-panel-header">
            <h6 className="view-panel-title">
              <i className="bi bi-people-fill" style={{ color: '#3b82f6' }} />
              All Accounts
            </h6>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span className="view-panel-count">{accounts.length}</span>
              <Link to="/account/create" style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px',
                borderRadius: 8, border: '1px solid rgba(59,130,246,0.3)', background: 'rgba(59,130,246,0.1)',
                color: '#60a5fa', fontSize: '0.8rem', fontWeight: 600, textDecoration: 'none',
                transition: 'background 0.18s, border-color 0.18s',
              }}>
                <i className="bi bi-person-plus-fill" /> New Account
              </Link>
            </div>
          </div>
          <MaterialReactTable table={table} />
        </div>
      </div>
    </>
  );
}
